import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const NAV_KEYS = ["home", "services", "machines", "gallery", "about", "contact"];

export function Header() {
  const { lang, t, pathFor, switchPath } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const linkCls = (key) => {
    const active = location.pathname === pathFor(key) || (key === "home" && location.pathname === `/${lang}`);
    return `relative px-1 py-2 text-sm font-medium transition-colors duration-200 ${active ? "text-white" : "text-slate-400 hover:text-white"}`;
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-anthracite/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
        <Link to={pathFor("home")} className="flex items-center gap-3" data-testid="logo-link">
          <img src="/logo-light.webp" alt="R-Mechanik SRLS Logo" className="h-12 w-auto" />
          <span className="hidden border border-white/15 px-2 py-0.5 font-mono-tech text-[10px] uppercase tracking-widest text-slate-400 md:block">
            BZ · 39041
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="desktop-nav">
          {NAV_KEYS.map((key) => (
            <Link key={key} to={pathFor(key)} className={linkCls(key)} data-testid={`nav-${key}`}>
              {t.nav[key]}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to={switchPath}
            className="border border-white/15 px-2.5 py-1.5 font-mono-tech text-xs tracking-widest text-slate-300 transition-colors hover:border-royal-soft hover:text-white"
            data-testid="lang-switcher"
          >
            {lang === "de" ? "DE | IT" : "IT | DE"}
          </Link>
          <Link
            to={pathFor("inquiry")}
            className="hidden bg-royal px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-royal-hover sm:block"
            data-testid="nav-inquiry-cta"
          >
            {t.nav.inquiry}
          </Link>
          <button
            className="p-2 text-white lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-white/10 bg-anthracite lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {NAV_KEYS.map((key) => (
                <Link
                  key={key}
                  to={pathFor(key)}
                  onClick={() => setOpen(false)}
                  className="px-2 py-3 text-base font-medium text-slate-200 hover:text-white"
                  data-testid={`mobile-nav-${key}`}
                >
                  {t.nav[key]}
                </Link>
              ))}
              <Link
                to={pathFor("inquiry")}
                onClick={() => setOpen(false)}
                className="mt-2 bg-royal px-4 py-3 text-center text-sm font-semibold text-white"
                data-testid="mobile-nav-inquiry-cta"
              >
                {t.nav.inquiry}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
